import { queryOptions } from '@tanstack/react-query';

import { ONE_HOUR } from '@/constants/time';
import { paths } from '@primum/api-types';
import { fetchClient } from '@/util/fetchClient';

type GetPublicContentLibraryItemV2PathParams =
  paths['/v2/public/content-library-items/{token}']['get']['parameters']['path'];

export type GetPublicContentLibraryItemV2Response = NonNullable<
  paths['/v2/public/content-library-items/{token}']['get']['responses'][200]['content']['application/json']['data']
>['contentLibraryItem'];

/**
 * Fetches a single Content Library item by its public share token.
 *
 * Calls the unauthenticated `/v2/public/content-library-items/{token}` endpoint. The
 * lookup is keyed solely on the opaque share token, so callers get the full item
 * (article body, video, download link) regardless of the item's own `isPublic` flag
 * or the visitor's auth state. `fetchClient` omits the Authorization header for
 * anonymous visitors and the endpoint ignores it when a signed-in user opens the
 * link. A revoked or unknown token returns 404, which `fetchClient` surfaces as a
 * thrown error for callers to render as an "unavailable" state.
 */
async function getPublicContentLibraryItemV2({ token }: GetPublicContentLibraryItemV2PathParams) {
  const { data } = await fetchClient.GET('/v2/public/content-library-items/{token}', {
    params: { path: { token } },
  });

  if (!data?.data) {
    throw new Error('Shared content library item not found');
  }

  return data.data.contentLibraryItem;
}

export const getPublicContentLibraryItemV2QueryOptions = ({
  token,
}: GetPublicContentLibraryItemV2PathParams) =>
  queryOptions<GetPublicContentLibraryItemV2Response>({
    queryKey: ['publicContentLibraryItem', token],
    queryFn: () => getPublicContentLibraryItemV2({ token }),
    staleTime: ONE_HOUR,
    gcTime: ONE_HOUR,
    enabled: !!token,
    retry: false,
  });
